/**
 * Snapshot de debug do runtime (pré-Service Worker).
 */

import { getStructuredTelemetry, getLastConflicts, getLastRecoveries, getExtendedEventCounts, getReplayEventCounts, getRuntimeModeTelemetry } from '../telemetry';
import { getCoordinationEpoch } from '../coordination/coordinationEpoch';
import { getFlushLockFencingToken, getFlushLockHolder } from '../coordination/distributedLock';
import { getLeaderFencingToken, readLeader } from '../coordination/distributedRecovery';
import { isLeaderTab, getTabId } from '../tabCoordinator';
import { getQueueLength } from '../queue/queueState';
import { getActiveReplaySession } from '../replay/replaySession';
import { getUnifiedPressureLevel } from './pressureModel';
import { getRuntimeMode } from './runtimeMode';
import { getPartitionSizes } from '../queue/partitioning';
import { getQueueMutable } from '../queue/queueState';
import { isStorageSafeMode, getStorageSafeModeReason } from '../storage/storageSafeMode';
import { readGenerationPointer } from '../storage/generationPointer';

export interface RuntimeDebugSnapshot {
  tabId: string;
  isLeader: boolean;
  owner: { tabId: string; epoch: number; fencingToken: number } | null;
  lockHolder: string | null;
  fencingToken: number;
  leaderFencingToken: number;
  epoch: number;
  queueSize: number;
  partitionSizes: Record<string, number>;
  replaySession: ReturnType<typeof getActiveReplaySession>;
  storageGeneration: number | null;
  runtimeMode: string;
  storageSafeMode: boolean;
  storageSafeReason: string | null;
  pressure: number;
  lastConflicts: ReturnType<typeof getLastConflicts>;
  lastRecoveries: ReturnType<typeof getLastRecoveries>;
  eventCounts: ReturnType<typeof getExtendedEventCounts>;
  replayCounts: ReturnType<typeof getReplayEventCounts>;
  telemetry: ReturnType<typeof getStructuredTelemetry>;
}

let cachedGeneration: number | null = null;

export async function hydrateDebugStorageGeneration(): Promise<void> {
  const meta = await readGenerationPointer();
  cachedGeneration = meta?.activeGeneration ?? null;
}

export function getRuntimeDebugSnapshot(): RuntimeDebugSnapshot {
  const leader = readLeader();
  return {
    tabId: getTabId(),
    isLeader: isLeaderTab(),
    owner: leader
      ? {
          tabId: leader.tabId,
          epoch: leader.epoch,
          fencingToken: leader.fencingToken,
        }
      : null,
    lockHolder: getFlushLockHolder(),
    fencingToken: getFlushLockFencingToken(),
    leaderFencingToken: getLeaderFencingToken(),
    epoch: getCoordinationEpoch(),
    queueSize: getQueueLength(),
    partitionSizes: getPartitionSizes(getQueueMutable()),
    replaySession: getActiveReplaySession(),
    storageGeneration: cachedGeneration,
    runtimeMode: getRuntimeModeTelemetry().mode || getRuntimeMode(),
    storageSafeMode: isStorageSafeMode(),
    storageSafeReason: getStorageSafeModeReason(),
    pressure: getUnifiedPressureLevel(),
    lastConflicts: getLastConflicts(),
    lastRecoveries: getLastRecoveries(),
    eventCounts: getExtendedEventCounts(),
    replayCounts: getReplayEventCounts(),
    telemetry: getStructuredTelemetry(),
  };
}
